// [목적] 앱 최상위 렌더 에러 바운더리 — 라우트/프로바이더 렌더 중 throw 를 잡아 흰 화면 대신 에러 화면 표시
// [이유] main.tsx 의 RouterProvider 가 ErrorBoundary 없이 마운트되어, LandingPage/HomePage 등 라우트 컴포넌트가
//         렌더 중 throw 하면 React 가 전체 트리를 unmount → 완전 빈 흰 화면(white screen of death)이 됨.
//         원인 진단이 불가하고 사용자 UX 도 최악이므로, 최상위에서 에러를 잡아 메시지를 화면에 노출.
// [사이드 이팩트] 순수 HTML + 인라인 스타일만 사용 — MUI/디자인토큰/라우터 등 어떤 모듈이 에러 원인이어도
//                 fallback 자체는 안전하게 렌더(의존성 0). dev 모드에서는 error.stack 까지 노출하여 즉시 진단.
// [수정 시 고려사항] 이 바운더리는 "최후의 그물" — 도메인별 ApiError UI 는 기존 ErrorBoundary(App 셸 내부)가 담당.
//                   여기서 토큰/MUI 를 쓰면 그 모듈이 에러 원인일 때 fallback 도 깨지므로 인라인 스타일 고수.
//                   prod 빌드에서는 stack 미노출(import.meta.env.DEV 가드).

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface RootErrorBoundaryProps {
  children: ReactNode;
}

interface RootErrorBoundaryState {
  error: Error | null;
}

/**
 * [목적] 최상위 렌더 에러를 잡아 흰 화면 대신 진단 가능한 에러 화면을 표시하는 class ErrorBoundary
 * [이유] React 의 에러 바운더리는 class 컴포넌트로만 구현 가능 (getDerivedStateFromError + componentDidCatch).
 * [사이드 이팩트] componentDidCatch 에서 console.error 로 스택 기록 — 브라우저 콘솔에서도 확인 가능.
 * [수정 시 고려사항] reset 은 location.reload() — 상태 복구가 아닌 전체 리로드로 단순화(최후 그물 성격).
 */
export class RootErrorBoundary extends Component<RootErrorBoundaryProps, RootErrorBoundaryState> {
  override state: RootErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RootErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // [이유] 콘솔에 전체 스택 + 컴포넌트 스택 기록 — 화면 노출과 별개로 개발자 추적 경로 확보
    // eslint-disable-next-line no-console
    console.error('[RootErrorBoundary] 렌더 에러:', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const isDev = Boolean(import.meta.env.DEV);
    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          padding: '32px 24px',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          color: '#29261B',
          background: '#FAF9F7',
          boxSizing: 'border-box',
        }}
      >
        <h1 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px' }}>
          화면을 그리는 중 오류가 발생했어요
        </h1>
        <p style={{ fontSize: 14, color: '#5C5849', margin: '0 0 16px' }}>
          아래 메시지를 개발자에게 전달하면 빠르게 고칠 수 있어요.
        </p>
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: 13,
            lineHeight: 1.5,
            background: '#FFFFFF',
            border: '1px solid #E8E2D5',
            borderRadius: 10,
            padding: '14px 16px',
            margin: '0 0 16px',
            color: '#B3261E',
          }}
        >
          {String(error.name)}: {String(error.message)}
        </pre>
        {isDev && error.stack ? (
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: 11,
              lineHeight: 1.5,
              background: '#FFFFFF',
              border: '1px solid #E8E2D5',
              borderRadius: 10,
              padding: '14px 16px',
              margin: '0 0 16px',
              color: '#5C5849',
              maxHeight: '40vh',
              overflow: 'auto',
            }}
          >
            {error.stack}
          </pre>
        ) : null}
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: '#FFFFFF',
            background: '#1F6FEB',
            border: 'none',
            borderRadius: 10,
            padding: '10px 18px',
            cursor: 'pointer',
          }}
        >
          새로고침
        </button>
      </div>
    );
  }
}
